import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Select } from "antd";
import { ChevronLeftIcon, Eye, EyeClosed, Save, X } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import useBranchRole from "../../hooks/useBranchRole";
import ToastMsg from "../../components/common/ToastMsg";
import { numericRecordId, truncateText } from "../../components/common/CommonFunctions";
import {
    normalizeId,
    sameId,
    blockOptionLabel,
    floorOptionLabel,
    ensureBlockOption,
    ensureFloorOption,
} from "./helpers/blockFloorHelpers";
import { titleEnum } from "./helpers/UserCreation";
import {
    BRANCH,
    EMPLOYEE_CANDIDATE_INFO,
    EMPLOYEE_DEPARTMENT,
    EMPLOYEE_DESIGNATION,
    EMPLOYEE_INTERN_INFO,
    EMPLOYEE_PERSONAL_INFO,
    EMPLOYEE_SHIFT,
    EMPLOYEE_TYPE,
    MASTER_BLOCK,
    MASTER_FLOOR,
    USERGROUP,
} from "../../constants/api";
import { isBranchManagementEnabled } from "../../utils/customConfigSession";

const { Option } = Select;

const REGISTER_TYPES = {
    employee: {
        label: "Employee",
        api: EMPLOYEE_PERSONAL_INFO,
        navigateTo: "/employee/viewDetails",
        buttonLabel: "Register Employee",
        banner:
            "Capture essential employee details and login credentials now. Profile, address, bank, and documents can be completed later.",
    },
    candidate: {
        label: "Candidate",
        api: EMPLOYEE_CANDIDATE_INFO,
        navigateTo: "/employee/onboarding/viewDetails",
        buttonLabel: "Register Candidate",
        banner:
            "Only name, mobile, department, and designation are required now. Additional onboarding steps can be completed later.",
    },
    intern: {
        label: "Intern",
        api: EMPLOYEE_INTERN_INFO,
        navigateTo: "/intern/onboarding/viewDetails",
        buttonLabel: "Register Intern",
        banner:
            "Only name, mobile, department, and designation are required now. Additional onboarding steps can be completed later.",
    },
};

const formInput = cn(
    "w-full rounded-md transition-colors duration-200",
    "border-gray-300 dark:border-gray-600",
    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20"
);

const formSelect = cn(
    "w-full rounded-md transition-colors duration-200",
    "border-gray-300 dark:border-gray-600",
    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20"
);

const defaultFormData = () => ({
    is_active: true,
    personal_info: {
        title: undefined,
        first_name: "",
        last_name: "",
        mobile_number: "",
    },
    professional_info: {
        department: undefined,
        designation: undefined,
        branch: undefined,
        work_block: undefined,
        work_floor: undefined,
        employee_type: undefined,
        shift: undefined,
        reporting_manager: undefined,
    },
    user_creation_info: {
        user_name: "",
        password: "",
        user_group: undefined,
        email_address: "",
    },
});

const QuickRegisterForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { fetch, create } = useApiMethods();
    const branchEnabled = isBranchManagementEnabled();
    const { isBranchManager, branchId: loginBranchId } = useBranchRole();

    const initialType = REGISTER_TYPES[searchParams.get("type")] ? searchParams.get("type") : "employee";
    const [registerType, setRegisterType] = useState(initialType);
    const [formData, setFormData] = useState(defaultFormData);
    const [dropdownData, setDropdownData] = useState({
        department: [],
        designation: [],
        empinfo: [],
        emptype: [],
        shift: [],
        usergroup: [],
        branch: [],
        block: [],
        floor: [],
    });
    const [loading, setLoading] = useState(false);
    const [screenLoader, setScreenLoader] = useState(true);

    const typeConfig = REGISTER_TYPES[registerType];
    const isEmployee = registerType === "employee";
    const isMinimal = registerType === "candidate" || registerType === "intern";
    const urlType = searchParams.get("type");
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    useEffect(() => {
        if (urlType && REGISTER_TYPES[urlType] && urlType !== registerType) {
            setRegisterType(urlType);
        }
    }, [urlType, registerType]);

    useEffect(() => {
        let cancelled = false;

        const loadDropdowns = async () => {
            setScreenLoader(true);
            try {
                const apiFetch = fetchRef.current;
                const requests = [
                    apiFetch(EMPLOYEE_DEPARTMENT, { drop_down: "True" }),
                    apiFetch(EMPLOYEE_DESIGNATION, { drop_down: "True" }),
                    apiFetch(BRANCH, { drop_down: "True" }).catch(() => ({ data: [] })),
                    apiFetch(MASTER_BLOCK, { drop_down: "True", is_active: "True" }).catch(() => ({ data: [] })),
                ];

                if (registerType === "employee") {
                    requests.push(
                        apiFetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" }),
                        apiFetch(EMPLOYEE_TYPE, { drop_down: "True" }),
                        apiFetch(EMPLOYEE_SHIFT, { drop_down: "True" }),
                        apiFetch(USERGROUP, { drop_down: "True" })
                    );
                }

                const results = await Promise.all(requests);
                if (cancelled) return;

                const next = {
                    department: results[0]?.data || [],
                    designation: results[1]?.data || [],
                    branch: results[2]?.data?.result || results[2]?.data || [],
                    block: results[3]?.data?.result || results[3]?.data || [],
                    floor: [],
                    empinfo: [],
                    emptype: [],
                    shift: [],
                    usergroup: [],
                };

                if (registerType === "employee") {
                    next.empinfo = results[4]?.data || [];
                    next.emptype = results[5]?.data || [];
                    next.shift = results[6]?.data || [];
                    next.usergroup = results[7]?.data || [];
                }

                setDropdownData(next);
            } catch (error) {
                if (!cancelled) {
                    ToastMsg("error", error.message || "Failed to load form options");
                }
            } finally {
                if (!cancelled) {
                    setScreenLoader(false);
                }
            }
        };

        loadDropdowns();

        return () => {
            cancelled = true;
        };
    }, [registerType]);

    const handleTypeChange = (type) => {
        if (!REGISTER_TYPES[type]) return;
        setRegisterType(type);
        setFormData(defaultFormData());
        navigate(`/employee/quick-register?type=${type}`, { replace: true });
    };

    const setField = (section, field, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value,
            },
        }));
    };

    const filteredBlocks = useMemo(() => {
        const blocks = dropdownData.block || [];
        if (!branchEnabled) return blocks;
        const branchId = formData.professional_info.branch;
        if (!branchId) return [];
        return blocks.filter((b) => !b.branch_id || sameId(b.branch_id, branchId));
    }, [dropdownData.block, branchEnabled, formData.professional_info.branch]);

    const blockOptions = useMemo(() => ensureBlockOption(
        filteredBlocks,
        formData.professional_info.work_block,
        formData.professional_info.block_name,
    ), [filteredBlocks, formData.professional_info.work_block, formData.professional_info.block_name]);

    const floorOptions = useMemo(() => ensureFloorOption(
        dropdownData.floor || [],
        formData.professional_info.work_floor,
        formData.professional_info.floor_name,
    ), [dropdownData.floor, formData.professional_info.work_floor, formData.professional_info.floor_name]);

    const selectedBlockLabel = useMemo(() => {
        const block = blockOptions.find((b) => sameId(b.id, formData.professional_info.work_block));
        return block ? blockOptionLabel(block) : "";
    }, [blockOptions, formData.professional_info.work_block]);

    const selectedFloorLabel = useMemo(() => {
        const floor = floorOptions.find((f) => sameId(f.id, formData.professional_info.work_floor));
        return floor ? floorOptionLabel(floor) : "";
    }, [floorOptions, formData.professional_info.work_floor]);

    const branchSelected = !branchEnabled || Boolean(formData.professional_info.branch);

    useEffect(() => {
        if (!branchEnabled || !isBranchManager || !loginBranchId) return;
        const branchKey = Number(loginBranchId) || loginBranchId;
        setFormData((prev) => {
            if (prev.professional_info.branch) return prev;
            return {
                ...prev,
                professional_info: {
                    ...prev.professional_info,
                    branch: branchKey,
                },
            };
        });
    }, [branchEnabled, isBranchManager, loginBranchId]);

    useEffect(() => {
        const branchId = formData.professional_info.branch;
        if (branchEnabled && !branchId) {
            setDropdownData((prev) => ({ ...prev, block: [] }));
            return;
        }
        let cancelled = false;
        const params = { drop_down: "True", is_active: "True" };
        if (branchEnabled && branchId) params.branch = branchId;
        fetchRef.current(MASTER_BLOCK, params)
            .then((res) => {
                if (cancelled) return;
                const blocks = res?.data?.result || res?.data || [];
                setDropdownData((prev) => ({ ...prev, block: Array.isArray(blocks) ? blocks : [] }));
            })
            .catch(() => {
                if (!cancelled) setDropdownData((prev) => ({ ...prev, block: [] }));
            });
        return () => { cancelled = true; };
    }, [formData.professional_info.branch, branchEnabled]);

    useEffect(() => {
        const blockId = formData.professional_info.work_block;
        if (!blockId) {
            setDropdownData((prev) => ({ ...prev, floor: [] }));
            return;
        }
        let cancelled = false;
        fetchRef.current(MASTER_FLOOR, { drop_down: "True", is_active: "True", block: blockId })
            .then((res) => {
                if (cancelled) return;
                const floors = res?.data?.result || res?.data || [];
                setDropdownData((prev) => ({ ...prev, floor: Array.isArray(floors) ? floors : [] }));
            })
            .catch(() => {
                if (!cancelled) setDropdownData((prev) => ({ ...prev, floor: [] }));
            });
        return () => { cancelled = true; };
    }, [formData.professional_info.work_block]);

    const handleBranchChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            professional_info: {
                ...prev.professional_info,
                branch: value,
                work_block: undefined,
                work_floor: undefined,
            },
        }));
    };

    const handleWorkBlockChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            professional_info: {
                ...prev.professional_info,
                work_block: normalizeId(value),
                work_floor: undefined,
                floor_name: null,
                block_name: blockOptions.find((b) => sameId(b.id, value))?.block_name || prev.professional_info.block_name,
            },
        }));
    };

    const validate = () => {
        const errors = [];
        const { personal_info, professional_info, user_creation_info } = formData;

        if (!personal_info.title) errors.push("Title is required");
        if (!personal_info.first_name?.trim()) errors.push("First Name is required");
        if (!personal_info.last_name?.trim()) errors.push("Last Name is required");
        if (!personal_info.mobile_number?.trim()) errors.push("Mobile Number is required");
        if (!professional_info.department) errors.push("Department is required");
        if (!professional_info.designation) errors.push("Designation is required");
        if (branchEnabled && !professional_info.branch) errors.push("Branch is required");

        if (personal_info.mobile_number && !/^\d{10}$/.test(personal_info.mobile_number.replace(/\D/g, ""))) {
            errors.push("Mobile Number should be 10 digits");
        }

        if (isEmployee) {
            if (!professional_info.employee_type) errors.push("Employee Type is required");
            if (!professional_info.shift) errors.push("Shift is required");
            if (!professional_info.reporting_manager) errors.push("Reporting Manager is required");
            if (!user_creation_info.user_name?.trim()) errors.push("Username is required");
            if (!user_creation_info.password?.trim()) errors.push("Password is required");
            if (!user_creation_info.user_group) errors.push("User Group is required");
            if (!user_creation_info.email_address?.trim()) errors.push("Professional Email is required");

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (user_creation_info.email_address && !emailRegex.test(user_creation_info.email_address)) {
                errors.push("Professional Email format is invalid");
            }
        }

        return errors;
    };

    const handleSave = async () => {
        const validationErrors = validate();
        if (validationErrors.length) {
            ToastMsg("error", validationErrors[0]);
            return;
        }

        setLoading(true);
        try {
            const professionalInfoCopy = {
                ...formData.professional_info,
                is_active: formData.is_active,
                display_name: `${formData.personal_info.first_name || ""} ${formData.personal_info.last_name || ""}`.trim(),
            };

            const personalInfoCopy = {
                ...formData.personal_info,
                is_quick_onboard: true,
            };

            if (isEmployee && formData.user_creation_info.email_address?.trim() && !personalInfoCopy.email_address?.trim()) {
                personalInfoCopy.email_address = formData.user_creation_info.email_address.trim();
            }

            const bankInfo = {
                bank_name: "",
                account_number: "",
                ifsc_code: "",
                account_holder_name: "",
                branch: "",
            };

            const userCreationInfo = isEmployee
                ? {
                    ...formData.user_creation_info,
                    is_admin: false,
                    is_branch_admin: false,
                    branch: formData.professional_info.branch || null,
                }
                : {
                    user_name: null,
                    password: null,
                    user_group: undefined,
                    email_address: null,
                };

            const formEmpData = new FormData();
            formEmpData.append("professional_info", JSON.stringify(professionalInfoCopy));
            formEmpData.append("personal_info", JSON.stringify(personalInfoCopy));
            formEmpData.append("bank_info", JSON.stringify(bankInfo));
            formEmpData.append("asset_info", JSON.stringify([]));
            formEmpData.append("user_creation_info", JSON.stringify(userCreationInfo));
            formEmpData.append("professional_info__profile", "");

            const response = await create(typeConfig.api, formEmpData, true, setLoading);

            if (response.status === "fail") {
                ToastMsg("error", response.message);
                return;
            }

            ToastMsg("success", response.message);
            setFormData(defaultFormData());
            navigate(typeConfig.navigateTo, {
                state: {
                    employeeID: numericRecordId(response.employee_id, response.employee?.id, response.id),
                },
            });
        } catch (error) {
            if (!error?.response) {
                ToastMsg("error", error.message || "An error occurred while saving");
            }
        } finally {
            setLoading(false);
        }
    };

    const typeButtons = useMemo(
        () => Object.entries(REGISTER_TYPES).map(([key, cfg]) => ({ key, label: cfg.label })),
        []
    );

    if (screenLoader) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
            </div>
        );
    }

    return (
        <ContainerCard>
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon className="h-5 w-5 cursor-pointer" onClick={() => navigate(-1)} />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Quick Register</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Register an employee, candidate, or intern with essential details only.
                    </p>
                </div>
                <ActiveToggle
                    isActive={formData.is_active}
                    onToggle={() => setFormData((prev) => ({ ...prev, is_active: !prev.is_active }))}
                />
            </div>

            <div className="mb-6 flex flex-wrap gap-2">
                {typeButtons.map(({ key, label }) => (
                    <Button
                        key={key}
                        type="button"
                        variant={registerType === key ? "default" : "outline"}
                        className={cn(
                            registerType === key && "bg-sidebar-primary hover:bg-sidebar-primary/90 text-white"
                        )}
                        onClick={() => handleTypeChange(key)}
                    >
                        {label}
                    </Button>
                ))}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                <div className="max-w-3xl mx-auto space-y-6">
                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
                        {typeConfig.banner}
                    </div>

                    <FormSection title="Basic Details">
                        <div className="grid grid-cols-1 gap-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="md:col-span-1">
                                    <FormField label="Title" required>
                                        <Select
                                            value={formData.personal_info.title}
                                            onChange={(value) => setField("personal_info", "title", value)}
                                            placeholder="Title"
                                            className={formSelect}
                                            size="large"
                                        >
                                            {titleEnum.map((title) => (
                                                <Option key={title.value} value={title.value}>
                                                    {title.text}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormField>
                                </div>
                                <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField label="First Name" required>
                                        <Input
                                            value={formData.personal_info.first_name}
                                            onChange={(e) => setField("personal_info", "first_name", e.target.value)}
                                            placeholder="First name"
                                            className={formInput}
                                            size="large"
                                        />
                                    </FormField>
                                    <FormField label="Last Name" required>
                                        <Input
                                            value={formData.personal_info.last_name}
                                            onChange={(e) => setField("personal_info", "last_name", e.target.value)}
                                            placeholder="Last name"
                                            className={formInput}
                                            size="large"
                                        />
                                    </FormField>
                                </div>
                            </div>

                            <FormField label="Mobile Number" required>
                                <Input
                                    value={formData.personal_info.mobile_number}
                                    onChange={(e) => setField("personal_info", "mobile_number", e.target.value)}
                                    placeholder="10-digit mobile number"
                                    className={formInput}
                                    size="large"
                                />
                            </FormField>
                        </div>
                    </FormSection>

                    <FormSection title={isMinimal ? `${typeConfig.label} Details` : "Professional Details"}>
                        <div className="grid grid-cols-1 gap-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField label="Department" required>
                                    <Select
                                        value={formData.professional_info.department}
                                        onChange={(value) => setField("professional_info", "department", value)}
                                        placeholder="Select department"
                                        className={formSelect}
                                        size="large"
                                    >
                                        {dropdownData.department.map((dept) => (
                                            <Option key={dept.id} value={dept.id}>
                                                {dept.department_name}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormField>

                                <FormField label="Designation" required>
                                    <Select
                                        value={formData.professional_info.designation}
                                        onChange={(value) => setField("professional_info", "designation", value)}
                                        placeholder="Select designation"
                                        className={formSelect}
                                        size="large"
                                    >
                                        {dropdownData.designation.map((desig) => (
                                            <Option key={desig.id} value={desig.id}>
                                                {desig.designation_name}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormField>
                            </div>

                            {isEmployee && (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormField label="Employee Type" required>
                                            <Select
                                                value={formData.professional_info.employee_type}
                                                onChange={(value) => setField("professional_info", "employee_type", value)}
                                                placeholder="Select employee type"
                                                className={formSelect}
                                                size="large"
                                            >
                                                {dropdownData.emptype.map((type) => (
                                                    <Option key={type.id} value={type.id}>
                                                        {type.employee_type_name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </FormField>

                                        <FormField label="Shift" required>
                                            <Select
                                                value={formData.professional_info.shift}
                                                onChange={(value) => setField("professional_info", "shift", value)}
                                                placeholder="Select shift"
                                                className={formSelect}
                                                size="large"
                                            >
                                                {dropdownData.shift.map((shift) => (
                                                    <Option key={shift.id} value={shift.id}>
                                                        {shift.shift_name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </FormField>
                                    </div>

                                    <FormField label="Reporting Manager" required>
                                        <Select
                                            value={formData.professional_info.reporting_manager}
                                            onChange={(value) => setField("professional_info", "reporting_manager", value)}
                                            placeholder="Select reporting manager"
                                            className={formSelect}
                                            size="large"
                                        >
                                            {dropdownData.empinfo.map((emp) => (
                                                <Option key={emp.id} value={emp.id}>
                                                    {`${emp.first_name} ${emp.last_name}`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </FormField>
                                </>
                            )}

                            {branchEnabled && (
                                <FormField label="Branch" required>
                                    <Select
                                        value={formData.professional_info.branch}
                                        onChange={handleBranchChange}
                                        placeholder="Select branch"
                                        className={formSelect}
                                        size="large"
                                        disabled={isBranchManager}
                                        allowClear={!isBranchManager}
                                    >
                                        {dropdownData.branch.map((br) => (
                                            <Option key={br.id} value={br.id}>
                                                {br.name}
                                                {br.is_main_branch ? " (Main)" : ""}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormField>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField label="Block">
                                    <Select
                                        value={formData.professional_info.work_block}
                                        onChange={handleWorkBlockChange}
                                        placeholder={branchSelected ? "Select block" : "Select branch first"}
                                        className={formSelect}
                                        size="large"
                                        allowClear
                                        disabled={!branchSelected}
                                        showSearch
                                        optionFilterProp="children"
                                        title={selectedBlockLabel}
                                    >
                                        {blockOptions.map((block) => {
                                            const label = blockOptionLabel(block);
                                            return (
                                                <Option key={block.id} value={normalizeId(block.id)} title={label}>
                                                    {truncateText(label, 35, "320px")}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </FormField>

                                <FormField label="Floor">
                                    <Select
                                        value={formData.professional_info.work_floor}
                                        onChange={(value) => setField("professional_info", "work_floor", normalizeId(value))}
                                        placeholder={formData.professional_info.work_block ? "Select floor" : "Select block first"}
                                        className={formSelect}
                                        size="large"
                                        allowClear
                                        disabled={!formData.professional_info.work_block}
                                        showSearch
                                        optionFilterProp="children"
                                        title={selectedFloorLabel}
                                    >
                                        {floorOptions.map((floor) => {
                                            const label = floorOptionLabel(floor);
                                            return (
                                                <Option key={floor.id} value={normalizeId(floor.id)} title={label}>
                                                    {truncateText(label, 35, "320px")}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </FormField>
                            </div>

                        </div>
                    </FormSection>

                    {isEmployee && (
                        <FormSection title="Login Credentials">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField label="Username" required>
                                    <Input
                                        value={formData.user_creation_info.user_name}
                                        onChange={(e) => setField("user_creation_info", "user_name", e.target.value)}
                                        placeholder="Enter username"
                                        className={formInput}
                                        size="large"
                                    />
                                </FormField>

                                <FormField label="Password" required>
                                    <PasswordInput
                                        value={formData.user_creation_info.password}
                                        onChange={(e) => setField("user_creation_info", "password", e.target.value)}
                                    />
                                </FormField>

                                <FormField label="User Group" required>
                                    <Select
                                        value={formData.user_creation_info.user_group}
                                        onChange={(value) => setField("user_creation_info", "user_group", value)}
                                        placeholder="Select user group"
                                        className={formSelect}
                                        size="large"
                                    >
                                        {dropdownData.usergroup.map((group) => (
                                            <Option key={group.id} value={group.id}>
                                                {group.user_group_name}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormField>

                                <FormField label="Professional Email" required>
                                    <Input
                                        value={formData.user_creation_info.email_address}
                                        onChange={(e) => setField("user_creation_info", "email_address", e.target.value)}
                                        placeholder="Enter professional email"
                                        className={formInput}
                                        size="large"
                                    />
                                </FormField>
                            </div>
                        </FormSection>
                    )}
                </div>
            </div>

            <div className="flex justify-end items-center gap-3 mt-3 p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <Button variant="outline" onClick={() => navigate(-1)} disabled={loading} className="flex items-center gap-2">
                    <X className="w-4 h-4" />
                    Cancel
                </Button>
                <Button
                    disabled={loading}
                    onClick={handleSave}
                    variant="brand"
                    className="flex items-center gap-2"
                >
                    {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                        <Save className="w-4 h-4" />
                    )}
                    {loading ? "Processing..." : typeConfig.buttonLabel}
                </Button>
            </div>
        </ContainerCard>
    );
};

const ActiveToggle = ({ isActive, onToggle }) => (
    <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600 dark:text-gray-400">{isActive ? "Active" : "Inactive"}</span>
        <button
            type="button"
            onClick={onToggle}
            className={cn(
                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                isActive ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600"
            )}
        >
            <span
                className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                    isActive ? "translate-x-6" : "translate-x-1"
                )}
            />
        </button>
    </div>
);

const FormSection = ({ title, children }) => (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 pb-3 border-b border-gray-200 dark:border-gray-700">
            {title}
        </h3>
        {children}
    </div>
);

const FormField = ({ label, required = false, children }) => (
    <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            {label}
            {required && <span className="text-red-500 text-xs">*</span>}
        </label>
        {children}
    </div>
);

const PasswordInput = ({ value, onChange }) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <div className="relative">
            <Input
                type={showPassword ? "text" : "password"}
                value={value}
                onChange={onChange}
                placeholder="Enter password"
                className={formInput}
                size="large"
            />
            <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-2 top-3 text-gray-500 hover:text-sidebar text-xs"
            >
                {showPassword ? <Eye className="w-4 h-4" /> : <EyeClosed className="w-4 h-4" />}
            </button>
        </div>
    );
};

export default QuickRegisterForm;
